// Import model
import { Note } from '../models/Note';

// Import base class serializer

// Import dependency types (models and enums used in properties)
import { Entity } from '../models/Entity';

// Import dependency serializers (for complex types)
import { EntitySerializer } from './EntitySerializer';

// Import derived type serializers (for polymorphic deserialization)
import { ContactNoteSerializer } from './ContactNoteSerializer';
import { LeadNoteSerializer } from './LeadNoteSerializer';
import { ProjectNoteSerializer } from './ProjectNoteSerializer';

/**
 * Serializer for Note - handles JSON serialization and deserialization with type validation
 */
export class NoteSerializer {
  /**
   * Deserializes a JSON string into a Note instance
   */
  static deserialize(json: string): Note {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a Note instance
   */
  static fromObject(obj: any): Note {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to Note');
    }

    // Handle polymorphic deserialization using discriminator
    const discriminator = obj['parentObjectType'];

    switch (discriminator) {
      case 'Contact':
        return ContactNoteSerializer.fromObject(obj);
      case 'salesforce.WorkOrder':
        return ProjectNoteSerializer.fromObject(obj);
      case 'Lead':
        return LeadNoteSerializer.fromObject(obj);
      default:
        // Fall through to base type deserialization
        break;
    }

    const instance: Note = {
      parentObjectType: obj['parentObjectType'] as string,
      parentObjectTypeName: obj['parentObjectType|Name'] as string | undefined,
      type_: obj['type'] as string | undefined,
      name: obj['name'] as string | undefined,
      description: obj['description'] as string | undefined,
      flowId: obj['flowId'] as string | undefined,
      flowIdName: obj['flowId|Name'] as string | undefined,
      objectStatusId: obj['objectStatusId'] as string | undefined,
      objectStatusIdName: obj['objectStatusId|Name'] as string | undefined,
      id: obj['_id'] as string | undefined,
      createdDate: obj['createdDate'] as string | undefined,
      lastModifiedDate: obj['lastModifiedDate'] as string | undefined,
      content: obj['content'] as string | undefined,
      contentType: obj['contentType'] as string | undefined,
      creatorId: obj['creatorId'] as string | undefined,
      creatorIdName: obj['creatorId|Name'] as string | undefined,
      entityId: obj['entityId'] as string | undefined,
      entityIdName: obj['entityId|Name'] as string | undefined,
      isActive: obj['isActive'] as boolean | undefined,
      relatedObjects: obj['relatedObjects'] as string | undefined,
      creator: obj['creator'] ? EntitySerializer.fromObject(obj['creator']) : undefined as any,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of Note instances
   */
  static deserializeArray(json: string): Note[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a Note instance to a JSON string
   */
  static serialize(instance: Note): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a Note instance to a plain object
   */
  static toObject(instance: Note): any {
    return {
      'parentObjectType': instance.parentObjectType,
      'parentObjectType|Name': instance.parentObjectTypeName,
      'type': instance.type_,
      'name': instance.name,
      'description': instance.description,
      'flowId': instance.flowId,
      'flowId|Name': instance.flowIdName,
      'objectStatusId': instance.objectStatusId,
      'objectStatusId|Name': instance.objectStatusIdName,
      '_id': instance.id,
      'createdDate': instance.createdDate,
      'lastModifiedDate': instance.lastModifiedDate,
      'content': instance.content,
      'contentType': instance.contentType,
      'creatorId': instance.creatorId,
      'creatorId|Name': instance.creatorIdName,
      'entityId': instance.entityId,
      'entityId|Name': instance.entityIdName,
      'isActive': instance.isActive,
      'relatedObjects': instance.relatedObjects,
      'creator': instance.creator ? EntitySerializer.toObject(instance.creator) : undefined,
    };
  }

  /**
   * Serializes an array of Note instances to a JSON string
   */
  static serializeArray(instances: Note[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
